import { enumerable } from '@Common/Core/CoreDecorator';

export interface ITableLayoutItem {
    rowsCount: number;
}

export default class AutoTableLayout<T extends ITableLayoutItem> {
    private columns: TableLayoutColumn<T>[];
    private rowsCount: number;

    constructor(private readonly items: T[], private columnsCount: number = 0) {
        this.initDimensions();
        this.initColumns();
    }

    public doLayout(): T[][] {
        for (let i = 0; i < this.items.length; i++) {
            const item = _.defaults(this.items[i], { rowsCount: 1 });
            this.columns[i % this.columnsCount].addItem(item);
        }

        for (const column of this.columns) {
            if (column.free < 0) {
                this.shareWithFreeColumns(column);
            }
        }

        return this.buildResultTable(this.columns, this.rowsCount);
    }

    private initDimensions(): void {
        const itemsRowsCount = this.items.map(item => getRowCount(item));
        const sum = _.sum(itemsRowsCount);
        const maxItem = Math.max(...itemsRowsCount);

        this.columnsCount = this.columnsCount || Math.ceil(Math.sqrt(sum));
        this.rowsCount = Math.max(Math.ceil(sum / this.columnsCount), maxItem);
    }

    private initColumns(): void {
        this.columns = [];
        for (let i = 0; i < this.columnsCount; i++) {
            this.columns.push(new TableLayoutColumn<T>(this.rowsCount));
        }
    }

    private buildResultTable(columns: TableLayoutColumn<T>[], rowCount: number): T[][] {
        const res: T[][] = [];
        for (let row = 0; row < rowCount; row++) {
            const rowArray = (res[row] = []);
            for (let column = 0; column < columns.length; column++) {
                rowArray[column] = columns[column].getRowItem(row);
            }
        }
        return res;
    }

    private shareWithFreeColumns(column: TableLayoutColumn<T>): void {
        for (let i = 0; i < column.items.length && column.free < 0; i++) {
            const item = column.items[i];
            const newColumn = this.columns.filter(newCol => newCol.free >= getRowCount(item))[0];
            if (newColumn) {
                newColumn.addItem(item);
                column.removeItem(item);
            }
        }
    }
}

class TableLayoutColumn<T extends ITableLayoutItem> {
    private innerItems: T[] = [];
    private fulledRows = 0;

    constructor(private maxRowCount: number) {}

    @enumerable
    public get free(): number {
        return this.maxRowCount - this.fulledRows;
    }

    @enumerable
    public get items(): T[] {
        return this.innerItems;
    }

    public removeItem(item: T): void {
        this.innerItems.remove(item);
        this.fulledRows -= getRowCount(item);
    }

    public addItem(item: T): void {
        this.innerItems.push(item);
        this.fulledRows += getRowCount(item);
    }

    public getRowItem(rowIndex: number): any {
        let rowCount = 0;
        for (const item of this.innerItems) {
            if (rowCount === rowIndex) {
                return item;
            }
            rowCount += getRowCount(item);
        }
        return null;
    }
}

function getRowCount(item: Partial<ITableLayoutItem>): number {
    return item.rowsCount || 1;
}
